import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { type BaseQueryApi } from '@reduxjs/toolkit/query';
import {
    type EntityMetadata,
    type EntityMetadataResponse,
    type ImageUploadResponse,
    type QueryFnBaseQuery,
    type S3Asset,
} from './ContentItemEditor.types';
import { uploadFiles } from './uploadFormData';

// snake_case, like the editors that call it.
const baseConfig = getBackRoyalApiBaseConfig('entityMetadataApi', { convertCasing: false });

export const entityMetadataApi = createApi('entityMetadataApi', {
    ...baseConfig,
    endpoints: builder => ({
        // The SEO metadata saves on its own request, before the content item. It has no optimistic
        // lock.
        updateEntityMetadata: builder.mutation<EntityMetadata, { record: EntityMetadata }>({
            query: ({ record }) => ({
                url: 'entity_metadata.json',
                method: 'PUT',
                body: { record },
            }),
            transformResponse: (response: EntityMetadataResponse) => {
                const record = response?.contents?.entity_metadata?.[0];
                if (!record) throw new Error('Unexpected response shape from the SEO metadata save');
                return record;
            },
            extraOptions: { maxRetries: 0 },
        }),

        uploadEntityMetadataImages: builder.mutation<S3Asset[], { entityMetadataId: string; files: File[] }>({
            async queryFn(
                { entityMetadataId, files },
                _api: BaseQueryApi,
                _options,
                fetchWithBaseQuery: QueryFnBaseQuery,
            ) {
                const result = await uploadFiles<ImageUploadResponse>(
                    { url: `entity_metadata/${entityMetadataId}/images.json`, key: 'images[]', files },
                    fetchWithBaseQuery,
                );
                if (result.error) return { error: result.error };
                return { data: result.data.contents.images };
            },
            extraOptions: { maxRetries: 0 },
        }),
    }),
});
